import React, { useState, useEffect } from "react";
import {
  applyLeave,
  getUsers,
  getLeaveTypes,
  getLeaveBalance,
} from "../../../api/api";
import ConfirmModal from "../../../components/ConfirmModal";
import SuccessModal from "../../../components/SuccessModal";

// ─── inject spin keyframe once ───────────────────────────────────
if (
  typeof document !== "undefined" &&
  !document.getElementById("__form-spin")
) {
  const s = document.createElement("style");
  s.id = "__form-spin";
  s.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}

export default function ApplyLeaveForm({
  balance: parentBalance = null,
  mobileNumber: parentMobileNumber = "",
  refreshBalance,
}) {
  const [users, setUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Parent normally owns the balance. Keep a local fallback only for
  // cases where this component is rendered without balance props.
  const [localBalance, setLocalBalance] = useState(null);

  const [search, setSearch] = useState("");
  const [ccSearch, setCCSearch] = useState("");

  // ── responsive ───────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener("resize", handler);

    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── initial data ─────────────────────────────────────────────
  useEffect(() => {
    fetchUsers();
    fetchLeaveTypes();

    // Do not make a duplicate balance request when the parent already
    // supplies the current balance.
    if (!parentBalance) {
      fetchBalance();
    }
  }, [parentBalance]);

  const fetchUsers = async () => {
  try {
    const data = await getUsers();

    setUsers(
      Array.isArray(data)
        ? data
        : Array.isArray(data?.users)
          ? data.users
          : []
    );
  } catch (error) {
    console.error("Failed to fetch users", error);
    setUsers([]);
  }
};

  const fetchLeaveTypes = async () => {
    try {
      const data = await getLeaveTypes();
      setLeaveTypes(data || []);
    } catch (error) {
      console.error("Failed to fetch leave types", error);
    }
  };

  const fetchBalance = async () => {
    try {
      const data = await getLeaveBalance();

      setLocalBalance(data || null);

      /*
       * Contact number is taken from the backend balance response.
       *
       * Example:
       * {
       *   mobile_number: "9876543210",
       *   total_balance: 5
       * }
       *
       * If mobile_number exists, it will automatically
       * populate the Contact Details field.
       *
       * The captain can still edit it afterwards.
       */
      const mobileNumber = String(
        parentMobileNumber || data?.mobile_number || ""
      )
        .replace(/\D/g, "")
        .slice(-10);

      if (mobileNumber.length === 10) {
        setForm((prev) => ({
          ...prev,
          contact: mobileNumber,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch balance", error);
    }
  };

  // Parent balance is authoritative when supplied.
  const balance = parentBalance || localBalance;

  // ── form ─────────────────────────────────────────────────────
  const [form, setForm] = useState({
    leave_type_id: "",
    fromDate: "",
    toDate: "",
    contact: "",
    reason: "",
    applyingTo: "",
    cc: [],
    files: [],
  });
  useEffect(() => {
  const mobileNumber = String(
    parentMobileNumber ||
    balance?.mobile_number ||
    ""
  )
    .replace(/\D/g, "")
    .slice(-10);

  if (mobileNumber.length === 10) {
    setForm((prev) => ({
      ...prev,
      contact: mobileNumber,
    }));
  }
}, [
  parentMobileNumber,
  balance?.mobile_number,
]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  // ===== CHANGE (Rule 1): state for the "No Leave Balance" popup =====
  const [noBalanceOpen, setNoBalanceOpen] = useState(false);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [success, setSuccess] = useState(false);
  const [leaveDays, setLeaveDays] = useState(0);

  const [showNotice, setShowNotice] = useState(true);

  const [showApplyDropdown, setShowApplyDropdown] = useState(false);
  const [showCCDropdown, setShowCCDropdown] = useState(false);

  // ── selected leave type ──────────────────────────────────────
  const selectedLeaveType = leaveTypes.find(
    (leaveType) =>
      Number(leaveType.id) === Number(form.leave_type_id)
  );

  const isSickLeave = selectedLeaveType?.category === "sick";

  // Manual LOP is allowed even when paid leave balance is zero.
  // Support both category and name because leave-type payloads can vary.
  const isLopLeaveType =
    selectedLeaveType?.category?.toString().toLowerCase() === "lop" ||
    selectedLeaveType?.name?.toString().toLowerCase().trim() === "lop";

  // ── balance values ───────────────────────────────────────────
  // Planned + Unplanned are treated as ONE combined leave balance
  // (Rule 5). total_balance from the API is trusted only if it
  // already represents that combined figure; otherwise it is
  // derived here.
  const plannedBalance = Number(
    balance?.planned_balance || 0
  );

  const unplannedBalance = Number(
    balance?.unplanned_balance || 0
  );

  const totalBalance = Number(
    balance?.total_balance ??
      plannedBalance + unplannedBalance
  );

  /*
   * ===== CHANGE: this now mirrors the REAL backend allocation
   * logic in LeaveApprovalService.process exactly, instead of a
   * naive combined-balance subtraction. That service floors each
   * bucket and deducts in a category-specific order:
   *   - "planned" requests: planned (floor) -> unplanned (floor) -> LOP
   *   - "unplanned" requests: unplanned (floor) -> planned (floor) -> LOP
   *   - any other category (e.g. "sick", "comp_off"): the real
   *     service deducts NOTHING from planned/unplanned — the full
   *     request becomes LOP on approval. Flagged to the team as
   *     worth confirming; shown accurately here rather than
   *     silently smoothed over.
   * Without this, the confirmation popup could show numbers that
   * don't match what actually happens when a manager approves.
   */
  const calculateAllocation = (
    category,
    requestedDays,
    plannedBal,
    unplannedBal
  ) => {
    const plannedAvailable = Math.floor(plannedBal);
    const unplannedAvailable = Math.floor(unplannedBal);

    let remaining = requestedDays;
    let plannedDays = 0;
    let unplannedDays = 0;

    if (category === "planned") {
      plannedDays = Math.min(remaining, plannedAvailable);
      remaining -= plannedDays;

      if (remaining > 0) {
        unplannedDays = Math.min(remaining, unplannedAvailable);
        remaining -= unplannedDays;
      }
    } else if (category === "unplanned") {
      unplannedDays = Math.min(remaining, unplannedAvailable);
      remaining -= unplannedDays;

      if (remaining > 0) {
        plannedDays = Math.min(remaining, plannedAvailable);
        remaining -= plannedDays;
      }
    }
    // else (sick, comp_off, etc.): matches the real service —
    // no deduction, remaining stays as-is, all of it becomes LOP.

    return {
      paidDays: plannedDays + unplannedDays,
      lopDays: Math.max(0, remaining),
    };
  };

  const allocation = calculateAllocation(
    selectedLeaveType?.category,
    leaveDays,
    plannedBalance,
    unplannedBalance
  );

  const lopDays = allocation.lopDays;
  const paidLeaveDays = allocation.paidDays;

  const isLopLeave =
    leaveDays > 0 && (lopDays > 0 || isLopLeaveType);

  // ===== CHANGE (Rule 1): employee has no leave balance at all =====
  const hasNoBalance = totalBalance <= 0;

  // ── calculate leave days ─────────────────────────────────────
  useEffect(() => {
    if (form.fromDate && form.toDate) {
      const start = new Date(form.fromDate);
      const end = new Date(form.toDate);

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const diff =
        (end - start) /
          (1000 * 60 * 60 * 24) +
        1;

      setLeaveDays(diff > 0 ? diff : 0);
    } else {
      setLeaveDays(0);
    }
  }, [form.fromDate, form.toDate]);

  // ── automatically select HR manager ──────────────────────────
  useEffect(() => {
    const user = JSON.parse(
      localStorage.getItem("user")
    );

    if (user?.hr_manager_id) {
      const manager = users.find(
        (u) =>
          Number(u.id) ===
          Number(user.hr_manager_id)
      );

      if (manager) {
        setForm((prev) => ({
          ...prev,
          applyingTo: `${manager.id} - ${manager.name}`,
        }));
      }
    }
  }, [users]);

  // ── handle input changes ─────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "contact") {
      /*
       * Allow only numbers and maximum 10 digits.
       *
       * This still allows the captain to edit the
       * automatically populated number.
       */
      if (/^\d{0,10}$/.test(value)) {
        setForm((prev) => ({
          ...prev,
          contact: value,
        }));

        // Remove contact error while typing
        setErrors((prev) => ({
          ...prev,
          contact: "",
        }));
      }

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ── file upload ──────────────────────────────────────────────
  const handleFileUpload = (e) => {
    setForm((prev) => ({
      ...prev,
      files: Array.from(e.target.files || []),
    }));
  };

  // ── CC ───────────────────────────────────────────────────────
  const addCCUser = (user) => {
    if (!form.cc.find((u) => u.id === user.id)) {
      setForm((prev) => ({
        ...prev,
        cc: [...prev.cc, user],
      }));
    }

    setShowCCDropdown(false);
  };

  const removeCC = (index) => {
    setForm((prev) => ({
      ...prev,
      cc: prev.cc.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // ── applying to ─────────────────────────────────────────────
  const selectApplyingTo = (user) => {
    setForm((prev) => ({
      ...prev,
      applyingTo: `${user.id} - ${user.name}`,
    }));

    setShowApplyDropdown(false);
  };

  // ── validation ──────────────────────────────────────────────
  const validate = () => {
    const err = {};

    if (!form.leave_type_id) {
      err.type = "Required";
    }

    if (!form.fromDate) {
      err.fromDate = "Required";
    }

    if (!form.toDate) {
      err.toDate = "Required";
    }

    // ─────────────────────────────────────────────
    // DATE VALIDATION
    // ─────────────────────────────────────────────
    if (form.fromDate && form.toDate) {
      const from = new Date(form.fromDate);
      const to = new Date(form.toDate);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);

      if (from < today) {
        err.fromDate =
          "Past dates are not allowed";
      }

      if (to < from) {
        err.toDate =
          "To Date cannot be before From Date";
      }
    }

    // ─────────────────────────────────────────────
    // CONTACT VALIDATION
    // ─────────────────────────────────────────────
    if (
      !form.contact ||
      form.contact.length !== 10
    ) {
      err.contact =
        "Enter valid 10 digit number";
    }

    if (!form.reason) {
      err.reason = "Required";
    }

    if (!form.applyingTo) {
      err.applyingTo = "Required";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };

  // ── actual API submission ────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError("");

    if (!validate()) {
      return false;
    }

    const user = JSON.parse(
      localStorage.getItem("user")
    );

    if (!user || !user.id) {
      setSubmitError(
        "User not found. Please login again."
      );

      return false;
    }

    const formData = new FormData();

    formData.append("user_id", user.id);

    formData.append(
      "leave_type_id",
      form.leave_type_id
    );

    formData.append(
      "from_date",
      form.fromDate
    );

    formData.append(
      "to_date",
      form.toDate
    );

    formData.append(
      "reason",
      form.reason
    );

    formData.append(
      "applying_to",
      form.applyingTo
    );

    formData.append(
      "contact",
      form.contact
    );

    formData.append(
      "status",
      "pending"
    );

    formData.append(
      "cc",
      JSON.stringify(
        form.cc.map((u) => u.email)
      )
    );

    if (
      isSickLeave &&
      form.files.length > 0
    ) {
      formData.append(
        "medical_file",
        form.files[0]
      );
    }

    try {
      setActionLoading(true);

      const res =
        await applyLeave(formData);

      // ===== CHANGE (Rule 6): the backend is authoritative.
      // If it rejects the request because of no balance, or any
      // other server-side validation error, surface that instead
      // of assuming success. =====
      if (res?.errors?.length) {
        setSubmitError(res.errors.join(", "));
        return false;
      }

      const success =
        !!res?.id ||
        (Array.isArray(res?.requests) && res.requests.length > 0) ||
        (Array.isArray(res?.leave_requests) &&
          res.leave_requests.length > 0) ||
        !!res?.success;

      if (!success) {
        setSubmitError(
          res?.message ||
          "Leave request could not be submitted."
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);

      const errorMessage =
        error?.errors?.join(", ") ||
        error?.response?.data?.errors?.join(", ") ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Leave already applied for selected dates";

      setSubmitError(errorMessage);

      setTimeout(() => {
        setSubmitError("");
      }, 5000);

      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // ── reset form ───────────────────────────────────────────────
  const resetForm = () => {
    /*
     * Keep the automatically fetched mobile number
     * after reset.
     */
    const mobileNumber = String(
      parentMobileNumber || balance?.mobile_number || ""
    )
      .replace(/\D/g, "")
      .slice(-10);

    setForm({
      leave_type_id: "",
      fromDate: "",
      toDate: "",
      contact:
        mobileNumber.length === 10
          ? mobileNumber
          : "",
      reason: "",
      applyingTo: "",
      cc: [],
      files: [],
    });

    setErrors({});
    setSuccess(false);
  };

  // ── open confirmation ───────────────────────────────────────
  const openConfirmation = () => {
    setSubmitError("");

    if (!validate()) {
      return;
    }

    // ===== CHANGE (Rule 1): no leave balance at all -> block
    // submission with the "No Leave Balance" popup instead of
    // ever showing the LOP/normal confirmation. =====
    if (hasNoBalance && !isLopLeaveType) {
      setNoBalanceOpen(true);
      return;
    }

    /*
     * Validation is complete and balance > 0.
     *
     * Now show confirmation.
     *
     * If requested leave exceeds balance,
     * ConfirmModal will display the LOP warning
     * (Rule 3). Otherwise it shows the normal
     * confirmation (Rule 2).
     */
    setConfirmOpen(true);
  };

  // ── derived styles ──────────────────────────────────────────
  const fullWidth = isMobile
    ? "100%"
    : "auto";

  // ── render ──────────────────────────────────────────────────
  return (
    <div style={styles.container}>

      {/* ─────────────────────────────────────────────
          NOTICE
      ───────────────────────────────────────────── */}
      {showNotice && (
        <div style={styles.notice}>
          <span>
            Leave is earned by employee as per policy.
          </span>

          <span
            style={styles.hide}
            onClick={() =>
              setShowNotice(false)
            }
          >
            Hide
          </span>
        </div>
      )}

      <h2
        style={{
          ...styles.title,
          fontSize: isMobile
            ? "18px"
            : "22px",
        }}
      >
        Applying for Leave
      </h2>

      {/* ─────────────────────────────────────────────
          BALANCE INFORMATION
      ───────────────────────────────────────────── */}
      <div style={styles.balanceCard}>
        <div style={styles.balanceItem}>
          <span style={styles.balanceLabel}>
            Planned Balance
          </span>

          <strong>
            {plannedBalance}
          </strong>
        </div>

        <div style={styles.balanceItem}>
          <span style={styles.balanceLabel}>
            Unplanned Balance
          </span>

          <strong>
            {unplannedBalance}
          </strong>
        </div>

        <div style={styles.balanceItem}>
          <span style={styles.balanceLabel}>
            Total Balance
          </span>

          <strong>
            {totalBalance}
          </strong>
        </div>

        {leaveDays > 0 && (
          <div
            style={{
              ...styles.balanceItem,
              ...(isLopLeave
                ? styles.lopBalanceItem
                : {}),
            }}
          >
            <span style={styles.balanceLabel}>
              Requested
            </span>

            <strong>
              {leaveDays} day
              {leaveDays !== 1
                ? "s"
                : ""}
            </strong>
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────
          SECTION 1: DATES
      ───────────────────────────────────────────── */}
      <div style={styles.card}>
        <div
          style={{
            ...styles.grid,
            gridTemplateColumns:
              isMobile
                ? "1fr"
                : "1fr 1fr",
          }}
        >
          <Field
            label="Leave Type"
            required
            error={errors.type}
          >
            <select
              name="leave_type_id"
              value={form.leave_type_id}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">
                Select Leave Type
              </option>

              {leaveTypes.map(
                (leaveType) => (
                  <option
                    key={leaveType.id}
                    value={leaveType.id}
                  >
                    {leaveType.name}
                  </option>
                )
              )}
            </select>
          </Field>

          <Field
            label="From Date"
            required
            error={errors.fromDate}
          >
            <input
              type="date"
              name="fromDate"
              value={form.fromDate}
              min={new Date()
                .toISOString()
                .split("T")[0]}
              onChange={handleChange}
              style={styles.input}
            />
          </Field>

          <Field
            label="To Date"
            required
            error={errors.toDate}
          >
            <input
              type="date"
              name="toDate"
              value={form.toDate}
              min={
                form.fromDate ||
                new Date()
                  .toISOString()
                  .split("T")[0]
              }
              onChange={handleChange}
              style={styles.input}
            />
          </Field>
        </div>

        {leaveDays > 0 && (
          <div style={styles.info}>
            Leave Days:{" "}
            <strong>{leaveDays}</strong>

            {isLopLeave && (
              <span style={styles.lopText}>
                {" "}
                • LOP Days:{" "}
                <strong>{lopDays}</strong>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────
          SECTION 2: PEOPLE + REASON
      ───────────────────────────────────────────── */}
      <div style={styles.card}>

        {/* APPLYING TO */}
        <Field
          label="Applying To"
          required
          error={errors.applyingTo}
        >
          <div style={styles.row}>
            <div
              style={{
                ...styles.applyBox,
                flex: 1,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {form.applyingTo ||
                "Select"}
            </div>

            {showApplyDropdown && (
              <div
                style={{
                  ...styles.dropdown,
                  width: isMobile
                    ? "calc(100vw - 60px)"
                    : "220px",
                }}
              >
                <input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  style={{
                    padding: "8px",
                    width: "90%",
                    boxSizing:
                      "border-box",
                  }}
                />

                {users
                  .filter((u) =>
                    String(
                      u.name || ""
                    )
                      .toLowerCase()
                      .includes(
                        search.toLowerCase()
                      )
                  )
                  .map((u) => (
                    <div
                      key={u.id}
                      style={
                        styles.dropdownItem
                      }
                      onClick={() =>
                        selectApplyingTo(u)
                      }
                    >
                      {u.id} - {u.name}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Field>

        {/* CC */}
        <div
          style={{
            marginTop: "15px",
          }}
        >
          <label style={styles.label}>
            CC to
          </label>

          <div style={styles.row}>
            <button
              type="button"
              style={styles.plusBtn}
              onClick={() =>
                setShowCCDropdown(
                  !showCCDropdown
                )
              }
            >
              +
            </button>

            {showCCDropdown && (
              <div
                style={{
                  ...styles.dropdown,
                  width: isMobile
                    ? "calc(100vw - 60px)"
                    : "220px",
                }}
              >
                <input
                  placeholder="Search..."
                  value={ccSearch}
                  onChange={(e) =>
                    setCCSearch(
                      e.target.value
                    )
                  }
                  style={{
                    padding: "8px",
                    width: "90%",
                    boxSizing:
                      "border-box",
                  }}
                />

                {users
                  .filter((u) =>
                    `${u.id} ${u.name}`
                      .toLowerCase()
                      .includes(
                        ccSearch.toLowerCase()
                      )
                  )
                  .map((u) => (
                    <div
                      key={u.id}
                      style={
                        styles.dropdownItem
                      }
                      onClick={() =>
                        addCCUser(u)
                      }
                    >
                      {u.id} - {u.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div style={styles.ccContainer}>
            {form.cc.map((c, i) => (
              <div
                key={i}
                style={styles.chip}
              >
                {c.name}

                <span
                  onClick={() =>
                    removeCC(i)
                  }
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ─────────────────────────────────────────
            CONTACT DETAILS
        ───────────────────────────────────────── */}
        <Field
          label="Contact Details"
          required
          error={errors.contact}
        >
          <div
            style={{
              ...styles.contactBox,
              ...(form.contact
                ? styles.contactBoxFilled
                : {}),
            }}
          >
            <span style={styles.prefix}>
              +91
            </span>

            <input
              name="contact"
              value={form.contact}
              onChange={handleChange}
              style={styles.contactInput}
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit number"
            />
          </div>

          {balance?.mobile_number &&
            form.contact && (
              <div
                style={
                  styles.contactHint
                }
              >
                Contact number was
                automatically filled from
                your profile. You can edit
                it if required.
              </div>
            )}
        </Field>

        {/* REASON */}
        <Field
          label="Reason"
          required
          error={errors.reason}
        >
          <textarea
            name="reason"
            value={form.reason}
            onChange={handleChange}
            placeholder="Enter a Reason"
            style={{
              ...styles.textareaFull,
              width: "100%",
              boxSizing:
                "border-box",
            }}
          />
        </Field>

        {/* MEDICAL FILE */}
        {isSickLeave && (
          <Field label="Attach Medical Files">
            <label
              style={
                styles.uploadLabel
              }
            >
              <span
                style={
                  styles.uploadIcon
                }
              >
                +
              </span>

              Attach File

              <input
                type="file"
                accept=".pdf,image/*"
                multiple
                onChange={
                  handleFileUpload
                }
                style={
                  styles.hiddenFileInput
                }
              />
            </label>

            {form.files.length > 0 && (
              <div
                style={
                  styles.fileList
                }
              >
                {form.files.map(
                  (file, index) => (
                    <div
                      key={index}
                      style={
                        styles.fileItem
                      }
                    >
                      {file.name}
                    </div>
                  )
                )}
              </div>
            )}
          </Field>
        )}
      </div>

      {/* ─────────────────────────────────────────────
          SUBMIT / CANCEL
      ───────────────────────────────────────────── */}
      <div style={styles.card}>
        {submitError && (
          <div
            style={{
              ...styles.errorBox,
              marginBottom: "15px",
            }}
          >
            {submitError}
          </div>
        )}

        <div
          style={{
            ...styles.actions,
            flexDirection: isMobile
              ? "column"
              : "row",
          }}
        >
          <button
            type="button"
            style={{
              ...styles.submit,
              width: fullWidth,
              opacity:
                actionLoading
                  ? 0.7
                  : 1,
            }}
            disabled={actionLoading}
            onClick={openConfirmation}
          >
            {actionLoading ? (
              <>
                <span
                  style={
                    styles.spinner
                  }
                />
                Submitting...
              </>
            ) : (
              "Submit Leave"
            )}
          </button>

          <button
            type="button"
            style={{
              ...styles.cancel,
              width: fullWidth,
            }}
            onClick={resetForm}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          CONFIRMATION MODAL (Rule 2 / Rule 3)
      ───────────────────────────────────────────── */}
      <ConfirmModal
        open={confirmOpen}
        title={
          isLopLeave
            ? "Leave Balance Exceeded"
            : "Confirm Leave Request"
        }
        message={
          isLopLeave
            // ===== CHANGE (Rule 3): message states BOTH the
            // paid-leave portion and the LOP portion, using the
            // actual computed allocation (paidLeaveDays) rather
            // than raw totalBalance — for a "sick"/"comp_off"
            // type request, paidLeaveDays can be 0 even when
            // totalBalance is positive, since that category
            // isn't deducted from Planned/Unplanned at all. =====
            ? `Based on your available leave balance, ${paidLeaveDays} day${
                paidLeaveDays !== 1
                  ? "s"
                  : ""
              } of your ${leaveDays}-day request can be treated as paid leave, and ${lopDays} day${
                lopDays !== 1
                  ? "s"
                  : ""
              } will be treated as Loss of Pay (LOP).

Are you sure you want to continue?`
            : `Please confirm that you want to submit this leave request for ${leaveDays} day${
                leaveDays !== 1
                  ? "s"
                  : ""
              }.`
        }
        confirmText={
          isLopLeave
            ? "Continue with LOP"
            : "Submit Leave"
        }
        confirmLoading={confirmLoading}
        onCancel={() =>
          setConfirmOpen(false)
        }
        onConfirm={async () => {
          setConfirmLoading(true);
          setLoading(true);

          const ok =
            await handleSubmit();

          setConfirmLoading(false);
          setLoading(false);
          setConfirmOpen(false);

          if (ok) {
            if (typeof refreshBalance === "function") {
              await refreshBalance();
            }

            resetForm();

            setSuccessOpen(true);

            setTimeout(() => {
              setSuccessOpen(false);
              window.location.href =
                "/home";
            }, 2000);
          }
        }}
      />

      {/* ─────────────────────────────────────────────
          NO LEAVE BALANCE MODAL (Rule 1)
          Self-contained so it doesn't depend on unknown
          internal props of the existing ConfirmModal
          component (e.g. whether it always renders a
          Cancel button, which we do not want here).
      ───────────────────────────────────────────── */}
      {noBalanceOpen && (
        <div style={styles.noBalanceOverlay}>
          <div style={styles.noBalanceBox}>
            <h3 style={styles.noBalanceTitle}>
              No Leave Balance
            </h3>

            <p style={styles.noBalanceMessage}>
              You have no available paid leave balance.
              You can still apply using LOP if LOP is
              selected as the leave type.
            </p>

            <button
              type="button"
              style={styles.noBalanceOkBtn}
              onClick={() =>
                setNoBalanceOpen(false)
              }
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          SUCCESS MODAL
      ───────────────────────────────────────────── */}
      <SuccessModal
        open={successOpen}
        message="Leave applied successfully"
        onClose={() => {
          setSuccessOpen(false);
          window.location.href =
            "/home";
        }}
      />
    </div>
  );
}

// ─── Field wrapper ───────────────────────────────────────────────
function Field({
  label,
  children,
  error,
  required,
}) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>
        {label}{" "}
        {required && (
          <span style={styles.star}>
            *
          </span>
        )}
      </label>

      {children}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const styles = {
  container: {
    background: "#f4f6f8",
    minHeight: "100vh",
    padding: "0px",
  },

  notice: {
    background: "#f0f0e6",
    color: "#070707",
    padding: "10px",
    borderRadius: "6px",
    marginBottom: "15px",
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    flexWrap: "wrap",
    gap: "8px",
  },

  hide: {
    cursor: "pointer",
    fontWeight: "bold",
    color: "blue",
  },

  title: {
    marginBottom: "20px",
    fontWeight: "700",
    color: "#111827",
  },

  card: {
    background: "#fff",
    padding: "16px",
    borderRadius: "10px",
    marginBottom: "18px",
    border: "1px solid #e0e0e0",
  },

  grid: {
    display: "grid",
    gap: "15px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    marginTop: "12px",
  },

  label: {
    marginBottom: "6px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#374151",
  },

  star: {
    color: "red",
  },

  input: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
  },

  textareaFull: {
    minHeight: "100px",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    resize: "none",
    fontSize: "14px",
  },

  info: {
    marginTop: "14px",
    background: "#f3f4f6",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
  },

  lopText: {
    color: "#dc2626",
    fontWeight: "700",
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    position: "relative",
  },

  plusBtn: {
    width: "34px",
    height: "34px",
    flexShrink: 0,
    borderRadius: "50%",
    border: "1px solid #ccc",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
  },

  dropdown: {
    position: "absolute",
    top: "40px",
    left: "0",
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: "8px",
    zIndex: 50,
    boxShadow:
      "0 8px 24px rgba(0,0,0,0.1)",
    maxHeight: "220px",
    overflowY: "auto",
  },

  dropdownItem: {
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13px",
    borderBottom:
      "1px solid #f3f4f6",
  },

  applyBox: {
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#374151",
    background: "#f9fafb",
  },

  ccContainer: {
    marginTop: "10px",
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },

  chip: {
    background: "#e0e0e0",
    padding: "6px 10px",
    borderRadius: "20px",
    display: "flex",
    gap: "6px",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "13px",
  },

  contactBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#fff",
  },

  contactBoxFilled: {
    border:
      "1px solid #93c5fd",
  },

  prefix: {
    padding: "10px 12px",
    background: "#f3f4f6",
    fontSize: "13px",
    whiteSpace: "nowrap",
  },

  contactInput: {
    border: "none",
    padding: "10px 12px",
    flex: 1,
    outline: "none",
    fontSize: "14px",
    minWidth: 0,
  },

  contactHint: {
    marginTop: "5px",
    fontSize: "11px",
    color: "#6b7280",
  },

  error: {
    fontSize: "12px",
    color: "#dc2626",
    marginTop: "4px",
  },

  errorBox: {
    background: "#fef2f2",
    color: "#b91c1c",
    border:
      "1px solid #fecaca",
    padding: "10px 12px",
    borderRadius: "8px",
    fontSize: "13px",
  },

  actions: {
    display: "flex",
    gap: "10px",
  },

  submit: {
    background: "#111827",
    color: "#fff",
    padding: "12px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  cancel: {
    background: "#e5e7eb",
    padding: "12px 20px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  },

  success: {
    marginTop: "10px",
    color: "#16a34a",
    fontWeight: "600",
  },

  spinner: {
    width: "16px",
    height: "16px",
    border:
      "2px solid rgba(255,255,255,0.4)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation:
      "spin 0.6s linear infinite",
  },

  uploadLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    background: "#e1e8f7",
    color: "#111827",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    width: "fit-content",
    minHeight: "44px",
  },

  hiddenFileInput: {
    display: "none",
  },

  fileList: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  fileItem: {
    padding: "10px 12px",
    background: "#f3f4f6",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#374151",
    wordBreak: "break-word",
  },

  uploadIcon: {
    fontSize: "20px",
    color: "#090909",
    fontWeight: "bold",
    lineHeight: 1,
  },

  balanceCard: {
    background: "#ffffff",
    border:
      "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "16px",
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },

  balanceItem: {
    background: "#f3f4f6",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#111827",
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    minWidth: "120px",
  },

  balanceLabel: {
    fontSize: "11px",
    color: "#6b7280",
    fontWeight: "500",
  },

  lopBalanceItem: {
    background: "#fef2f2",
    border:
      "1px solid #fecaca",
    color: "#b91c1c",
  },

  // ===== CHANGE (Rule 1): styles for the new No Leave Balance modal =====
  noBalanceOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(17, 24, 39, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },

  noBalanceBox: {
    background: "#fff",
    borderRadius: "12px",
    padding: "22px",
    maxWidth: "380px",
    width: "100%",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },

  noBalanceTitle: {
    margin: 0,
    marginBottom: "10px",
    color: "#b91c1c",
    fontSize: "17px",
    fontWeight: 700,
  },

  noBalanceMessage: {
    margin: 0,
    marginBottom: "18px",
    color: "#374151",
    fontSize: "14px",
    lineHeight: 1.5,
  },

  noBalanceOkBtn: {
    background: "#111827",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 18px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    width: "100%",
  },
};